'use client';

import { channelLabel } from '@components/messages/channel-label';
import { useQuery } from '@rocicorp/zero/react';
import { useMemo } from 'react';
import { queries } from '../zero/queries';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OrgChannel = any;

type UseOrgChannelsOptions = {
  /** Skip the query (e.g. no read permission, or a closed dialog). */
  enabled?: boolean;
  /**
   * Channels before direct messages, then:
   * - `label`: by the displayed label (DMs by participant names), as the
   *   channel sidebar shows them;
   * - `name`: by the stored name, as the messages landing picks its first
   *   channel;
   * - `none`: query order.
   */
  sort?: 'label' | 'name' | 'none';
  /** Viewer and agent label for `label` sorting. */
  viewerUserId?: string;
  agentLabel?: string;
};

const EMPTY: OrgChannel[] = [];

/**
 * The organization's channel list (`organizations.withChannels`), live.
 * Zero can't order a junction relationship, so sorting happens here.
 */
export function useOrgChannels(
  organizationId: string,
  {
    enabled = true,
    sort = 'none',
    viewerUserId,
    agentLabel
  }: UseOrgChannelsOptions = {}
) {
  const [organization, result] = useQuery(
    enabled ? queries.organizations.withChannels({ organizationId }) : undefined
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rows = (organization as any)?.chatChannels as OrgChannel[] | undefined;
  const channels = useMemo(() => {
    if (!rows) return EMPTY;
    if (sort === 'none') return rows;
    const key =
      sort === 'label'
        ? (channel: OrgChannel) =>
            channelLabel(channel, viewerUserId, agentLabel)
        : (channel: OrgChannel) => channel.name ?? '';
    return [...rows].sort(
      (a, b) =>
        Number(a.dmChannel) - Number(b.dmChannel) ||
        key(a).localeCompare(key(b))
    );
  }, [rows, sort, viewerUserId, agentLabel]);
  return { organization, channels, result };
}
